import React, {useEffect, useState} from 'react';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import InfoHover from '../../../../shared-components/InfoHover';
import {settingsTableProps, SETTINGS_TABLE_TOP_ROW_SX, SMALL_BLUE_BUTTON_SX} from '../SettingsPills';
import {COMPACT_TABLE_PROPS} from '../../Profiles/ProfileTabs/FormsTab/formsTableStyle';
import {
    useAddFormCategoryMutation,
    useDeleteFormCategoryMutation,
    useGetFormCategoriesQuery,
    useUpdateFormCategoryMutation
} from '../../../../api/services/formCategoriesService';
import useNotification from '../../../../utility/hooks/useNotification';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CategoryIcon from '@mui/icons-material/Category';
import {Box, Button, FormControl, FormLabel, Stack, TextField, Typography} from '@mui/material';
import {CirclePicker} from 'react-color';
import * as muiColors from '@mui/material/colors';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import {validateFieldLength} from '../../../../utility/validationUtil';
import useTextField from '../../../../utility/hooks/useTextField';
import CircleIcon from '@mui/icons-material/Circle';
import {useConfirm} from 'material-ui-confirm';

/* eslint-disable react/prop-types */
const columns = [
    {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({
                   cell,
                   row
               }) =>
            <Stack direction="row" alignItems="center" gap={1}>
                <CircleIcon fontSize="small" style={{color: row.original.color}}/>
                <Typography>{cell.getValue()}</Typography>
            </Stack>,
    }
];
/* eslint-enable react/prop-types */

const colors = Object.values(muiColors)
    .filter(color => color[300])
    .map(color => color[300]);

const FormCategories = () => {
    const {
        data,
        isLoading,
        isFetching,
        isError,
        error
    } = useGetFormCategoriesQuery();
    const {
        showErrorNotification,
        showSuccessNotification,
        showDetailedErrorNotification
    } = useNotification();
    const [editId, setEditId] = useState();
    const [color, setColor] = useState();
    const [openCreateFormCategoryDialog, setOpenCreateFormCategoryDialog] = useState(false);
    const [openEditFormCategoryDialog, setOpenEditFormCategoryDialog] = useState(false);
    const nameField = useTextField({
        initialValue: '',
        validate: (value) => validateFieldLength(value, 100)
    });
    const [createCategory] = useAddFormCategoryMutation();
    const [updateCategory] = useUpdateFormCategoryMutation();
    const [deleteCategory] = useDeleteFormCategoryMutation();
    const confirm = useConfirm();

    useEffect(() => {
        if (!isError) return;

        const msg =
            error?.data?.message ??
            error?.error ??
            'Failed to load form categories';

        showErrorNotification(msg);
    }, [isError, error, showErrorNotification]);

    const handleChangeComplete = (color) => {
        setColor(color.hex);
    };

    const handleEdit = (params) => {
        nameField.setValue(params.name);
        setColor(params.color);
        setEditId(params.id);
        setOpenEditFormCategoryDialog(true);
    };

    const handleDelete = async (params) => {
        try {
            await confirm({
                title: 'Confirm Deletion',
                description: (
                    <>
                        Are you sure you want to delete this category?
                        <br/><br/>
                        <strong>{params.name}</strong>
                    </>
                ),
                confirmationText: 'Delete',
                cancellationText: 'Cancel',
            });

            await deleteCategory({formCategoryId: params.id}).unwrap();
            showSuccessNotification('Form category deleted');
        } catch (e) {
            if (e) {
                showErrorNotification('Failed to delete form category');
            }
        }
    };

    const handleDialogClose = () => {
        setOpenCreateFormCategoryDialog(false);
        setOpenEditFormCategoryDialog(false);
        nameField.reset();
        setColor(undefined);
        setEditId(undefined);
    };

    const handleSaveCategory = async () => {
        const isCreate = openCreateFormCategoryDialog;
        const isEdit = openEditFormCategoryDialog;

        const func = isCreate ? createCategory : isEdit ? updateCategory : null;
        if (!func) return;

        try {
            await func({
                name: nameField.value,
                color,
                ...(isEdit ? {formCategoryId: editId} : {}),
            }).unwrap();

            showSuccessNotification('Form category saved');
            handleDialogClose();
        } catch (e) {
            showDetailedErrorNotification(
                'Failed to save form category',
                e?.data?.validationErrors
            );
        }
    };

    return (
        <>
            {/* Maria, 20 October 2026: a smaller 'New Category' button, with the table's icons on the same row */}
            <Box sx={SETTINGS_TABLE_TOP_ROW_SX}>
                <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon/>}
                    onClick={() => setOpenCreateFormCategoryDialog(true)}
                    sx={SMALL_BLUE_BUTTON_SX}
                >
                    New category
                </Button>
                {/* Maria, 20 October 2026: the words from the Creating and Managing Categories video script */}
                <InfoHover
                    testId="hover-categories"
                    text="Categories are used to organise and streamline different types of forms and groups. Assigning categories to forms and groups makes them easier to manage and filter. The more categories you create, the more filter options you have. To create a category, click on the New Category button, then choose a name and colour. To edit or delete a category, click on the three dot Actions menu."
                />
            </Box>

            <AdmicityTableV2
                columns={columns}
                data={data ?? []}
                isLoading={isLoading}
                isRefetching={isFetching}
                initialState={{
                    columnFilters: [],
                    expanded: {},
                    sorting: [{
                        id: 'name',
                        desc: false
                    }],
                }}
                rowActions={[
                    {
                        label: 'Edit',
                        action: handleEdit
                    },
                    {
                        label: 'Delete',
                        action: handleDelete
                    }
                ]}
                noRowsOverlay={{
                    icon: <CategoryIcon/>,
                    text: 'No categories have been added'
                }}
                tableProps={{
                    // Maria, 20 October 2026: in the style of the other School Settings tables
                    ...settingsTableProps(COMPACT_TABLE_PROPS),
                    manualSorting: false,
                    enableSorting: true,
                    enableFilters: false,
                    enableColumnResizing: false,
                    enableBottomToolbar: false,
                }}
            />
            <AdmicityDialog
                handleClose={handleDialogClose}
                title={openCreateFormCategoryDialog ? 'Create new form category' : 'Update  form category'}
                open={openCreateFormCategoryDialog || openEditFormCategoryDialog}
                actions={[
                    {
                        label: 'Cancel',
                        onClick: handleDialogClose
                    },
                    {
                        label: 'Save',
                        disabled: !color || !nameField.value,
                        onClick: handleSaveCategory
                    }
                ]}
            >
                <TextField
                    required
                    fullWidth
                    label="Category Name"
                    variant="outlined"
                    margin="normal"
                    {...nameField}
                />
                <FormControl
                    error={error}
                    fullWidth
                    margin="normal"
                    required
                >
                    <FormLabel>Pick color:</FormLabel>
                    <Box marginBottom={1} marginTop={1}>
                        <CirclePicker
                            color={color}
                            colors={colors}
                            onChangeComplete={handleChangeComplete}
                        />
                    </Box>
                </FormControl>
            </AdmicityDialog>
        </>
    );
};

export default FormCategories;